import {
  TalentSearchClaim,
  useGetTalentSearchUserContextQuery,
} from 'src/graphql/generated';
import type { DashboardTalentSearchUserContext } from 'src/types';

import { useAccountContext } from './useAccountContext';

const getTalentSearchContext = (
  claims: TalentSearchClaim[],
  productType?: TalentSearchClaim | null,
): DashboardTalentSearchUserContext => ({
  claims,
  productType,
  hasPTSClaim:
    claims?.find(
      (claim) =>
        // Until we can display custom tiles to users with CTS, they will be treated like PTS customers
        claim === TalentSearchClaim.PREMIUM_TALENT_SEARCH ||
        claim === TalentSearchClaim.CONNECTED_TALENT_SEARCH,
    ) !== undefined,
});

// This might return NULL if this data has not been set up for a hirer
export const useTalentSearchContext = () => {
  const { data: accountContext } = useAccountContext();
  const skip = !accountContext;

  // Even if accountContext is still null we need to call this hook
  // (with skip: true) to comply with the rule of hooks in React
  const { loading, error, data } = useGetTalentSearchUserContextQuery({
    skip,
    context: {
      // This query expects advertiserId in the header, not as a variable
      headers: {
        advertiserId: accountContext?.currentAdvertiser.seekId,
      },
    },
  });

  // We often get a 'not found' error from this endpoint.
  // Rather than having our APP think we're still loading, we return no TS claims
  const claims = error ? [] : data?.talentSearchUserContext?.claims;

  return {
    // Force loading to come back as 'true' if we don't have the account info yet
    loading: skip || loading,
    error,
    // This can actually be NULL from the graph if it hasn't been set up
    data: claims
      ? getTalentSearchContext(
          claims,
          data?.talentSearchUserContext?.productType,
        )
      : null,
  };
};
